import { jsx as _jsx } from "react/jsx-runtime";
import { NavLink, Navigate } from 'react-router-dom';
import { t } from '@/core/localization';
import { appSections } from '../appSections';
import { ProtectedRoute } from '../components';
const { rootPath, linkTo, title, claimIds } = appSections.settings;
export const settingsRoute = {
    path: rootPath,
    element: _jsx(ProtectedRoute, { claims: claimIds }),
    handle: {
        crumb: () => _jsx(NavLink, { to: linkTo, children: title }),
    },
    children: [
        {
            index: true,
            lazy: async () => {
                const { Settings } = await import('@/admin/modules/settings');
                return { Component: Settings };
            },
        },
        // General route
        {
            path: 'general',
            lazy: async () => {
                const { GeneralSettings } = await import('@/admin/modules/settings/generalSettings');
                return { Component: GeneralSettings };
            },
            handle: {
                crumb: () => _jsx(NavLink, { to: `${linkTo}/general`, children: t('general.settings.btn') }),
            },
        },
        // Warehouses route
        {
            path: 'warehouses',
            handle: {
                crumb: () => _jsx(NavLink, { to: `${linkTo}/warehouses`, children: t('warehouses.label') }),
            },
            children: [
                {
                    index: true,
                    element: _jsx(Navigate, { to: "stock", replace: true }),
                },
                {
                    path: 'stock',
                    handle: {
                        crumb: () => _jsx(NavLink, { to: `${linkTo}/warehouses/stock`, children: t('stock.label') }),
                    },
                    lazy: async () => {
                        const { Warehouses } = await import('@/admin/modules/settings/warehouses');
                        return { Component: Warehouses };
                    },
                    children: [
                        {
                            index: true,
                            lazy: async () => {
                                const { StockSettingsTable } = await import('@/admin/modules/settings/warehouses/components/StockSettingsTable');
                                return { Component: StockSettingsTable };
                            },
                        },
                    ],
                },
                {
                    // This (second) 'stock' path is only necessary for 'Stock' breadcrumb before `stockId`.
                    // In other way it should be just 'stock/:stockId'.
                    path: 'stock',
                    handle: {
                        crumb: () => _jsx(NavLink, { to: `${linkTo}/warehouses/stock`, children: t('stock.label') }),
                    },
                    children: [
                        {
                            path: ':stockId',
                            lazy: async () => {
                                const { StockSettingEdit } = await import('@/admin/modules/settings/warehouses/components/StockSettingEdit');
                                return { Component: StockSettingEdit };
                            },
                            handle: {
                                crumb: ({ params }) => (_jsx(NavLink, { to: `${linkTo}/warehouses/stock/${params.stockId}`, children: params.stockId === 'add' ? t('stock.add.breadcrumb.label') : t('stock.edit.breadcrumb.label') })),
                            },
                        },
                    ],
                },
                {
                    path: 'crossdocking',
                    handle: {
                        crumb: () => _jsx(NavLink, { to: `${linkTo}warehouses/crossdocking`, children: t('crossdocking.label') }),
                    },
                    lazy: async () => {
                        const { Warehouses } = await import('@/admin/modules/settings/warehouses');
                        return { Component: Warehouses };
                    },
                    children: [
                        {
                            index: true,
                            lazy: async () => {
                                const { CrossdockTable } = await import('@/admin/modules/settings/warehouses/components/CrossdockTable');
                                return { Component: CrossdockTable };
                            },
                        },
                    ],
                },
                {
                    // This (second) 'crossdocking' path is only necessary for 'Crossdocking' breadcrumb before `stockId`.
                    // In other way it should be just 'crossdocking/:stockId'.
                    path: 'crossdocking',
                    handle: {
                        crumb: () => _jsx(NavLink, { to: `${linkTo}/warehouses/crossdocking`, children: t('crossdocking.label') }),
                    },
                    children: [
                        {
                            path: ':stockId',
                            lazy: async () => {
                                const { CrossdockEdit } = await import('@/admin/modules/settings/warehouses/components/CrossdockEdit');
                                return { Component: CrossdockEdit };
                            },
                            handle: {
                                crumb: ({ params }) => (_jsx(NavLink, { to: `${linkTo}/warehouses/stock/${params.stockId}`, children: params.stockId === 'add' ? t('stock.add.breadcrumb.label') : t('stock.edit.breadcrumb.label') })),
                            },
                        },
                    ],
                },
            ],
        },
        // Storage route
        {
            path: 'storage',
            handle: {
                crumb: () => _jsx(NavLink, { to: `${linkTo}/storage`, children: t('warehouse.addresses.label') }),
            },
            children: [
                {
                    index: true,
                    lazy: async () => {
                        const { Storage } = await import('@/admin/modules/settings/storage/Storage');
                        return { Component: Storage };
                    },
                },
                {
                    path: ':sector',
                    lazy: async () => {
                        const { StorageSectorEdit } = await import('@/admin/modules/settings/storage/components');
                        return { Component: StorageSectorEdit };
                    },
                    handle: {
                        crumb: ({ params }) => (_jsx(NavLink, { to: `${linkTo}/storage/${params.sector}`, children: params.sector === 'add' ? t('storage.sector.add.breadcrumb.label') : t('storage.edit.sector.label') })),
                    },
                },
                {
                    path: ':sector/view',
                    lazy: async () => {
                        const { StorageSectorView } = await import('@/admin/modules/settings/storage/components');
                        return { Component: StorageSectorView };
                    },
                    handle: {
                        crumb: ({ params }) => (_jsx(NavLink, { to: `${linkTo}/storage/${params.sector}/view`, children: t('storage.view.sector.label') })),
                    },
                },
            ],
        },
        // Identity route
        {
            path: 'identity',
            lazy: async () => {
                const { Identity } = await import('@/admin/modules/settings/identity');
                return { Component: Identity };
            },
            children: [
                {
                    index: true,
                    // TODO: check if 'redirect' can be used.
                    lazy: async () => {
                        const { Users } = await import('@/admin/modules/settings/identity/components/Users');
                        return { Component: Users };
                    },
                },
                {
                    path: 'users',
                    handle: {
                        crumb: () => _jsx(NavLink, { to: `${linkTo}/identity/users`, children: t('users.settings.btn') }),
                    },
                    children: [
                        {
                            index: true,
                            lazy: async () => {
                                const { Users } = await import('@/admin/modules/settings/identity/components/Users');
                                return { Component: Users };
                            },
                        },
                    ],
                },
                {
                    path: 'roles',
                    handle: {
                        crumb: () => _jsx(NavLink, { to: `${linkTo}/identity/roles`, children: t('users.settings.btn') }),
                    },
                    children: [
                        {
                            index: true,
                            lazy: async () => {
                                const { Roles } = await import('@/admin/modules/settings/identity/components/Roles');
                                return { Component: Roles };
                            },
                        },
                    ],
                },
            ],
        },
        {
            path: 'identity/users',
            handle: {
                crumb: () => _jsx(NavLink, { to: `${linkTo}/identity/users`, children: t('users.settings.btn') }),
            },
            children: [
                {
                    path: ':userId',
                    lazy: async () => {
                        const { UserEdit } = await import('@/admin/modules/settings/identity/components/UserEdit');
                        return { Component: UserEdit };
                    },
                    handle: {
                        crumb: ({ params }) => (_jsx(NavLink, { to: `${linkTo}/identity/users/${params.userId}`, children: params.userId === 'add' ? t('user.creation.label') : t('user.editing.label') })),
                    },
                },
            ],
        },
        {
            path: 'identity/roles',
            handle: {
                crumb: () => _jsx(NavLink, { to: `${linkTo}/identity/roles`, children: t('users.settings.btn') }),
            },
            children: [
                {
                    path: ':roleId',
                    lazy: async () => {
                        const { RoleEdit } = await import('@/admin/modules/settings/identity/components/RoleEdit');
                        return { Component: RoleEdit };
                    },
                    handle: {
                        crumb: ({ params }) => (_jsx(NavLink, { to: `${linkTo}/identity/roles/${params.roleId}`, children: params.roleId === 'add' ? t('role.creation.label') : t('role.editing.label') })),
                    },
                },
            ],
        },
        // Locations route
        {
            path: 'locations',
            handle: {
                crumb: () => _jsx(NavLink, { to: `${linkTo}/locations`, children: t('locations.label') }),
            },
            children: [
                {
                    index: true,
                    lazy: async () => {
                        const { Locations } = await import('@/admin/modules/settings/locations');
                        return { Component: Locations };
                    },
                },
                {
                    path: ':locationId',
                    lazy: async () => {
                        const { LocationEdit } = await import('@/admin/modules/settings/locations/components/LocationEdit');
                        return { Component: LocationEdit };
                    },
                    handle: {
                        crumb: ({ params }) => (_jsx(NavLink, { to: `${linkTo}/locations/${params.locationId}`, children: params.locationId === 'add' ? t('creating.a.location.header') : t('edit.location.label') })),
                    },
                },
            ],
        },
        // Drivers route
        {
            path: 'drivers',
            handle: {
                crumb: () => _jsx(NavLink, { to: `${linkTo}/drivers`, children: t('drivers.label') }),
            },
            children: [
                {
                    index: true,
                    lazy: async () => {
                        const { Drivers } = await import('@/admin/modules/settings/drivers');
                        return { Component: Drivers };
                    },
                },
                {
                    path: ':driverId',
                    lazy: async () => {
                        const { DriverEdit } = await import('@/admin/modules/settings/drivers/components/DriverEdit');
                        return { Component: DriverEdit };
                    },
                    handle: {
                        crumb: ({ params }) => (_jsx(NavLink, { to: `${linkTo}/drivers/${params.driverId}`, children: params.driverId === 'add' ? t('new.driver.creation.breadcrumb.title') : t('driver.edit.breadcrumb.title') })),
                    },
                },
            ],
        },
    ],
};
